import React, { useState } from 'react';
import T from 'utils/useLocalizer'

import PageHeader from 'components/features/orders/PageHeader';
import HorizontalDivider from 'components/ui/divider/HorizontalDivider';

import { useExportOrdersToCsvFile, useGetOrdersList } from 'services/orders/ordersServices';
import OrdersList from 'components/features/orders/OrdersList';
import OrderToolbar from 'components/features/orders/toolbar/OrderToolbar';
import { useLanguage } from 'utils/useLanguage';
import useNotify from 'utils/useNotify';

const ORDER_PAGE_SIZE = 10;

const Orders = () => {
  const { language } = useLanguage();
  const { notifyInfo, notifyError, notifySuccess } = useNotify();
  const [isExportFileLoading, setIsExportFileLoading] = useState<boolean>(false)

  const {
    data: orders,
    fetchNextPage,
    isLoading,
    isError,
    hasNextPage
  } = useGetOrdersList(ORDER_PAGE_SIZE, language);

  const {
    data,
    isLoading: isExportLoading,
    isError: exportError,
    refetch
  } = useExportOrdersToCsvFile(language);

  const resources = {
    pageTitle: T('orders.page.title'),
    pageSubTitle: T('orders.page.description'),
    notifyNoOrderToExportMessage: T('orders.no.order.to.export.message'),
    notifyExportCsvErrorMessage: T('orders.export.error.message'),
    notifySuccessExportMessage: T('orders.export.success.message')
  };

  const exportOrderToCsvFile = async () => {
    setIsExportFileLoading(true)
    if (orders?.pages[0]?.totalCount) {   
      try {
        const { data } = await refetch();

        if (!data) {
          notifyError(resources.notifyExportCsvErrorMessage);
          return;
        }

        // Ensure it's a Blob regardless of what Axios returns
        const blob = data instanceof Blob
          ? data
          : new Blob([JSON.stringify(data)], { type: 'text/csv' });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Orders_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        notifySuccess(resources.notifySuccessExportMessage)
      } catch (error) {
        notifyError(resources.notifyExportCsvErrorMessage);
      } finally { setIsExportFileLoading(false) }
    } else {
      notifyInfo(resources.notifyNoOrderToExportMessage);
      setIsExportFileLoading(false)
    }
  };
  
  return (
    <div className="container">
      <PageHeader title={resources.pageTitle} subTitle={resources.pageSubTitle} />

      <HorizontalDivider spacing="lg" />
      <OrderToolbar exportOrderToCsvFile={exportOrderToCsvFile} isLoading={isExportFileLoading} />

      <OrdersList
        pages={orders?.pages ?? []}
        fetchMoreHandler={fetchNextPage}
        hasMore={hasNextPage}
        isLoading={isLoading}
        isError={isError}
      />
    </div>
  );
};

export default Orders;